import { LoadingButton } from '@mui/lab';
import { Button, Grid, MenuItem, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import OberonDialog from '~components/OberonDialog';
import { Transfer, TransferType, TransferTypeValues, UpdateTransfer } from '~hooks/useTransfers/domain';
import { transferTypeList, validateArn, validatePhoneNumber } from '~pages/SystemManagement/TransferList/helper';

type EditTransferModalProps = {
  open: Boolean;
  transfer?: Transfer;
  onAccept: (transferId: number, data: UpdateTransfer) => void;
  onClose: () => void;
};

function EditTransferModal({ transfer, open, onAccept, onClose }: EditTransferModalProps) {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const {
    formState: { errors },
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
  } = useForm<UpdateTransfer>({
    mode: 'all',
    reValidateMode: 'onChange',
    shouldUnregister: true,
  });
  const watchType = watch('type');
  const endpointValidationObj = {
    required: 'Endpoint is required.',
    validate: watchType ? (watchType === TransferType.ARN ? validateArn : validatePhoneNumber) : undefined,
  };

  useEffect(() => {
    if (open && transfer) {
      setValue('name', transfer.name);
      setValue('type', transfer.type as TransferTypeValues);
      setValue('endpoint', transfer.endpoint);
      setValue('description', transfer.description || '');
    }

    // Reset form on close
    return function cleanupEditTransferModal() {
      reset();
    };
  }, [open, transfer]);

  const onSubmit = handleSubmit(async (data: UpdateTransfer) => {
    setSubmitting(true);

    try {
      await onAccept(transfer?.id!, data);
    } catch (e) {
      // Do nothing, catch error to prevent form reset on failed action
      return;
    } finally {
      setSubmitting(false);
    }

    reset();
  });

  const transferListDisplay = transferTypeList.map((item) => (
    <MenuItem key={item.value} value={item.value}>
      {item.label}
    </MenuItem>
  ));

  return (
    <OberonDialog
      title='Edit Transfer'
      open={open && Boolean(transfer)}
      onSubmit={onSubmit}
      content={
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Controller
              name='name'
              control={control}
              rules={{ required: 'Name is required.' }}
              render={({ field }) => (
                <TextField
                  fullWidth
                  variant='outlined'
                  label='Name'
                  disabled={submitting}
                  required={true}
                  error={Boolean(errors.name)}
                  helperText={errors.name?.message}
                  {...field}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name='type'
              control={control}
              rules={{ required: 'Type is required.' }}
              render={({ field }) => (
                <TextField
                  fullWidth
                  variant='outlined'
                  label='Type'
                  select
                  disabled={submitting}
                  required={true}
                  error={Boolean(errors.type)}
                  {...field}>
                  {transferListDisplay}
                </TextField>
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name='endpoint'
              control={control}
              rules={endpointValidationObj}
              render={({ field }) => (
                <TextField
                  fullWidth
                  variant='outlined'
                  label='Endpoint'
                  disabled={submitting}
                  required={true}
                  error={Boolean(errors.endpoint)}
                  helperText={errors.endpoint?.message}
                  {...field}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name='description'
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  variant='outlined'
                  label='Description'
                  disabled={submitting}
                  error={Boolean(errors.description)}
                  helperText={errors.description?.message}
                  {...field}
                />
              )}
            />
          </Grid>
        </Grid>
      }
      actionFooter={
        <>
          <Button
            variant='text'
            disabled={submitting}
            onClick={() => {
              onClose();
            }}>
            Close
          </Button>

          <LoadingButton
            type='submit'
            variant='contained'
            disableElevation
            color='primary'
            disabled={submitting}
            loading={submitting}>
            Update
          </LoadingButton>
        </>
      }
    />
  );
}

export default EditTransferModal;
